import React, { useRef, useState } from 'react';

/** Tabela local: um único contêiner mantém o cabeçalho fixo e alinhado às colunas. */
export default function ResizableTable({
  columns,
  rows,
  label,
  rowKey,
  offset = 0,
  renderRowDetails,
  rowProps,
  rowHeading,
}) {
  const [widths, setWidths] = useState(() => columns.map((column) => column.width));
  const drag = useRef(null);
  const resize = (index, width) => {
    const column = columns[index];
    setWidths((current) =>
      current.map((value, i) =>
        i === index
          ? Math.min(column.maxWidth || 2400, Math.max(column.minWidth || 60, width))
          : value,
      ),
    );
  };
  return (
    <div className="tc-k-table-scroll" role="region" aria-label={label} tabIndex={0}>
      <table
        className="tc-k-table"
        style={{ width: widths.reduce((sum, width) => sum + width, 0) }}
      >
        <caption className="sr-only">{label}</caption>
        <colgroup>
          {columns.map((column, i) => (
            <col key={column.key} style={{ width: widths[i] }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((column, i) => (
              <th key={column.key} scope="col" title={column.label}>
                <span className="tc-k-cell-text">{column.label}</span>
                {column.resizable !== false && (
                  <span
                    className="tc-k-resize"
                    role="separator"
                    tabIndex={0}
                    aria-label={`Redimensionar coluna ${column.label}`}
                    aria-orientation="vertical"
                    aria-valuemin={column.minWidth || 60}
                    aria-valuemax={column.maxWidth || 2400}
                    aria-valuenow={Math.round(widths[i])}
                    aria-valuetext={`${Math.round(widths[i])} pixels`}
                    title="Arraste para ajustar. Use as setas ← →; Shift acelera. Duplo clique restaura."
                    onPointerDown={(event) => {
                      if (event.button !== 0) return;
                      event.preventDefault();
                      event.currentTarget.focus({ preventScroll: true });
                      event.currentTarget.setPointerCapture(event.pointerId);
                      drag.current = {
                        id: event.pointerId,
                        index: i,
                        x: event.clientX,
                        width: widths[i],
                      };
                    }}
                    onPointerMove={(event) => {
                      const start = drag.current;
                      if (start?.id === event.pointerId && start.index === i)
                        resize(i, start.width + event.clientX - start.x);
                    }}
                    onPointerUp={(event) => {
                      if (event.currentTarget.hasPointerCapture(event.pointerId))
                        event.currentTarget.releasePointerCapture(event.pointerId);
                      drag.current = null;
                    }}
                    onPointerCancel={() => {
                      drag.current = null;
                    }}
                    onLostPointerCapture={() => {
                      drag.current = null;
                    }}
                    onDoubleClick={() => resize(i, column.width)}
                    onKeyDown={(event) => {
                      const step = event.shiftKey ? 64 : 16;
                      const width = {
                        ArrowLeft: widths[i] - step,
                        ArrowRight: widths[i] + step,
                        Home: column.minWidth || 60,
                        End: column.maxWidth || 2400,
                      }[event.key];
                      if (width !== undefined) {
                        event.preventDefault();
                        resize(i, width);
                      }
                    }}
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const details = renderRowDetails?.(row);
            return (
              <React.Fragment key={rowKey(row)}>
                {rowHeading?.(row, index) && (
                  <tr className="tc-k-row-heading">
                    <th colSpan={columns.length} scope="rowgroup">
                      {rowHeading(row, index)}
                    </th>
                  </tr>
                )}
                <tr data-row-key={rowKey(row)} {...rowProps?.(row)}>
                  {columns.map((column) => {
                    const value = column.value(row, offset + index);
                    const content = column.render
                      ? column.render(row, value, offset + index)
                      : value;
                    return (
                      <td key={column.key} className={column.className}>
                        <div className="tc-k-cell-content">
                          <span className="tc-k-cell-text" title={String(value)}>
                            {content}
                          </span>
                          {column.addon?.(row)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
                {details != null && (
                  <tr className="tc-k-row-details" data-details-for={rowKey(row)}>
                    <td colSpan={columns.length}>{details}</td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
